import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { AppContext } from '../AppContext';

const fmtUSD = (cents) =>
  (Number(cents || 0) / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export default function MyOrders() {
  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();
  const { isLoggedIn } = useContext(AppContext);

  const [uid, setUid] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Ensure we have a uid (uses Auth directly so this page works even if AppContext
  // doesn't expose the uid)
  useEffect(() => {
    const off = onAuthStateChanged(auth, (u) => setUid(u ? u.uid : null));
    return () => off();
  }, [auth]);



  // Attach listener to the user's orders subcollection doc:
  // users/{uid}/streetFlavorOrders/merchOrders  →  { orders: [...] }
  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    const ref = doc(db, 'users', uid, 'streetFlavorOrders', 'merchOrders');

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        const arr = Array.isArray(data.orders) ? data.orders.slice() : [];
        // Sort newest first by createdAt if present
        arr.sort((a, b) => {
          const ta = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const tb = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return tb - ta;
        });
        setOrders(arr);
        setLoading(false);
      },
      (err) => {
        console.error('[MyOrders] snapshot error', err);
        setOrders([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [db, uid]);

  return (
    <div className="sf-orders-page">
      <h1 className="sf-title">My Orders</h1>
      <div className="sf-actions-top">
        <button className="sf-btn ghost" onClick={() => navigate('/shop')}>Back to Shop</button>
      </div>

      <div className="sf-card">
        {loading ? (
          <p>Loading your orders…</p>
        ) : !orders.length ? (
          <div className="sf-empty">
            <p>No orders yet.</p>
            <button className="sf-btn" onClick={() => navigate('/shop')}>Start Shopping</button>
          </div>
        ) : (
          <ul className="sf-orders">
            {orders.map((o) => {
              const created = o?.createdAt?.toDate ? o.createdAt.toDate() : null;
const status = o?.orderStatus === 'shipped'
  ? 'Shipped'
  : (o?.orderFulfilled ? 'Fulfilled' : 'Processing');

const statusClass = status === 'Shipped' || status === 'Fulfilled' ? 'ok' : 'pending';

              return (
                <li key={o.paymentIntentId || Math.random()} className="sf-order">
                  <div className="sf-order-head">
                    <div className="left">
                      <div className="sf-order-id">Order: {o.paymentIntentId || '—'}</div>
                      <div className="sf-order-date">
                        {created ? created.toLocaleString() : '—'}
                      </div>
                    </div>
<div className={`sf-status ${statusClass}`}>
  {status}
</div>

                  </div>

                  <div className="sf-order-grid">
                    <div className="sf-order-box">
                      <div className="sf-order-title">Items</div>
                      <ul className="sf-order-items">
                        {(o.items || []).map((it, idx) => (
                          <li key={idx} className="sf-order-item">
                            <div className="left">
                              <div className="name">{it.name || it.id}</div>
                              <div className="meta">Qty {it.qty}</div>
                            </div>
                            <div className="right">{fmtUSD(it.unitCents)}</div>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="sf-order-box">
                      <div className="sf-order-title">Totals</div>
                      <div className="sf-order-totals">
                        <div><span>Subtotal</span><span>{fmtUSD(o?.totals?.subtotalCents)}</span></div>
                        <div><span>Shipping</span><span>{fmtUSD(o?.totals?.shippingCents)}</span></div>
                        <div><span>Tax</span><span>{fmtUSD(o?.totals?.taxCents)}</span></div>
                        {o?.totals?.walletAppliedCents > 0 && (
                          <div><span>Wallet Applied</span><span>-{fmtUSD(o?.totals?.walletAppliedCents)}</span></div>
                        )}
                        <div className="sf-order-total">
                          <span>Charged</span>
                          <span>{fmtUSD(o?.totals?.chargedCents)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="sf-order-box">
                      <div className="sf-order-title">Ship To</div>
                      <div className="sf-order-ship">
                        <div className="name">{o?.shipping?.name}</div>
                        <div>{o?.shipping?.address?.line1}</div>
                        {o?.shipping?.address?.line2 ? <div>{o.shipping.address.line2}</div> : null}
                        <div>
                          {o?.shipping?.address?.city}{o?.shipping?.address?.city ? ', ' : ''}
                          {o?.shipping?.address?.state} {o?.shipping?.address?.postal_code}
                        </div>
                        <div>{o?.shipping?.address?.country}</div>
                      </div>
                    </div>

                    <div className="sf-order-box">
                      <div className="sf-order-title">Contact</div>
                      <div className="sf-order-contact">
                        <div>{o?.contact?.name}</div>
                        <div>{o?.contact?.email}</div>
                        <div>{o?.contact?.phone}</div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <style>{`
:root{
  --sf-gold:#f5c65d;
  --sf-gold-10:rgba(245,198,93,.10);
  --sf-panel:#0b0b0b;
  --sf-bd:rgba(255,255,255,.12);
  --sf-bd-2:rgba(255,255,255,.18);
}
body { background:#000; color:#fff; font-family:'CustomFont',sans-serif; }
.sf-orders-page{ max-width:1024px; margin:18px auto 80px; padding:0 16px; }
.sf-title{ font-size:2rem; font-weight:900; text-shadow:0 0 16px var(--sf-gold-10); margin:4px 0 14px; }
.sf-actions-top{ margin-bottom:12px; }
.sf-btn{
  padding:.68rem 1.15rem; border-radius:12px; font-weight:900; cursor:pointer;
  border:2px solid var(--sf-gold); background:var(--sf-gold); color:#000;
  box-shadow:0 6px 18px rgba(245,198,93,.26);
}
.sf-btn.ghost{
  background:transparent; color:var(--sf-gold);
  border:1px solid rgba(255,255,255,.25); box-shadow:none;
}
.sf-card{
  background:var(--sf-panel); border:1px solid var(--sf-bd); border-radius:14px; padding:16px;
}
.sf-empty{ text-align:center; }
.sf-orders{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:14px; }
.sf-order{
  border:1px solid var(--sf-bd-2); border-radius:12px; padding:12px; background:#0b0b0b;
}
.sf-order-head{ display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:10px; }
.sf-order-id{ font-weight:900; }
.sf-order-date{ opacity:.85; font-size:.94rem; }
.sf-status{ padding:.35rem .6rem; border-radius:999px; font-weight:900; border:1px solid var(--sf-bd-2); }
.sf-status.ok{ background:#0f2714; border-color:#1f5f2b; color:#9CF3B4; }
.sf-status.pending{ background:#1f1a05; border-color:#665a1a; color:#f5c65d; }

.sf-order-grid{ display:grid; gap:10px; grid-template-columns: 2fr 1.2fr 1.2fr 1fr; }
@media (max-width: 920px){ .sf-order-grid{ grid-template-columns: 1fr; } }

.sf-order-box{ border:1px solid var(--sf-bd); border-radius:10px; padding:10px; background:#0f0f0f; }
.sf-order-title{ font-weight:900; margin-bottom:8px; }

.sf-order-items{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:8px; }
.sf-order-item{ display:flex; justify-content:space-between; align-items:center; gap:10px; }
.sf-order-item .name{ font-weight:800; }
.sf-order-item .meta{ opacity:.85; font-size:.9rem; }

.sf-order-totals{ display:flex; flex-direction:column; gap:6px; }
.sf-order-totals > div{ display:flex; justify-content:space-between; }
.sf-order-total{ font-weight:900; color:var(--sf-gold); }

.sf-order-ship, .sf-order-contact{ display:flex; flex-direction:column; gap:4px; }
.sf-order-ship .name{ font-weight:800; }
      `}</style>
    </div>
  );
}
